/* eslint-disable no-console */
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

interface CustomFormattedMessageProps {
  id: string;
  defaultMessage: string;
  values?: {
    [key: string]: React.ReactNode | ((children: React.ReactNode[]) => React.ReactNode);
  };
}

const FormattedMessage: React.FC<CustomFormattedMessageProps> = ({ id, defaultMessage = '', values = {} }) => {
  const { t } = useTranslation();
  const template = t(id, defaultMessage);
  if (!template) {
    console.error(`Missing translation for key: ${id}`);
  }

  const renderContent = (content: string): React.ReactNode[] => {
    const elements: React.ReactNode[] = [];
    const stack: { tag: string; children: React.ReactNode[] }[] = [];
    const regex = /(\{[^\{\}]+\}|<[^<>]+\/?>|[^<{}]+(?=<|$))/gi;

    content.split(regex).forEach((part, index) => {
      if (part.startsWith('{') && part.endsWith('}')) {
        // Handle replacement tokens
        const key = part.slice(1, -1);
        const element = (values[key] as React.ReactNode) || key;
        (stack.length > 0 ? stack[stack.length - 1].children : elements).push(
          <Fragment key={`token-${index}`}>{element}</Fragment>,
        );
      } else if (part.startsWith('<') && !part.startsWith('</')) {
        // Handle opening tags
        const tag = part.replace(/<|\/?>/g, '');
        if (tag === 'br') {
          // Handle self-closing tag
          (stack.length > 0 ? stack[stack.length - 1].children : elements).push(<br key={index} />);
        } else {
          stack.push({ tag, children: [] });
        }
      } else if (part.startsWith('</')) {
        // Handle closing tags
        const tag = part.replace(/<\/|>/g, '');
        const node = stack.pop();
        if (!node || node.tag !== tag) return;
        const value = values[node.tag];
        const element = value instanceof Function ? value(node.children) : <>{node.children}</>;
        (stack.length > 0 ? stack[stack.length - 1].children : elements).push(
          <Fragment key={`tag-${tag}-${index}`}>{element}</Fragment>,
        );
      } else if (part.trim()) {
        // Handle plain text
        (stack.length > 0 ? stack[stack.length - 1].children : elements).push(part);
      }
    });

    return elements;
  };

  return <>{renderContent(template)}</>;
};
export default FormattedMessage;
